import { createForm, useCreateFormStore } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { IconReplace } from '@components/icons/replace';
import { IconTextAnonymize } from '@components/icons/text-anonymize';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { SelectOption } from '@components/select';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingVariables } from '@components/text-editors/addons/field-mapping';
import { EditorValue } from '@components/text-editors/text-editor';
import { useScopedTranslate } from '@locale/translate';

import {
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import {
  JourneyActionValuesTypes,
  MappableActionVariable,
  TravellerJourneyActionDetailed,
} from 'api/types';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';
import { actionErrorTitle } from 'ui/traveller-journey-builder/node-types/common';

import {
  ActionProgressBar,
  EditableActionContent,
  FormSidebarFooter,
  MappedSource,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  TriggerMethod,
  isActionError,
  useEditingContext,
} from '../common';
import { getFieldMappingId } from '../helpers';

type ActionType = 'anonymize_text';

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<ActionType>(shortId);
  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !travellerActionQuery.data) {
    return <div>{t('dataNotFound')}</div>;
  }
  const action = travellerActionQuery.data;
  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={t('title')}
        icon={IconTextAnonymize}
        iconColor="elementIndigo100"
      >
        <TravellerActionActions action={action} />
      </Sidebar.SimpleHeader>
      <Sidebar.Content>
        <EditableActionContent
          edit={<FormContent action={action} />}
          display={<DetailsContent action={action} />}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<ActionType>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const referenceVariables = action.values?.input?.variables ?? {};
  const firstReferenceVariable = Object.values(referenceVariables)[0];
  const referenceVariablesKeys = Object.keys(referenceVariables);
  const isAttributeReference =
    referenceVariablesKeys.length > 0 &&
    (firstReferenceVariable.source.startsWith('company_traveller_attribute') ||
      firstReferenceVariable.source.startsWith('journey_attribute'));
  const isTravellerReference = firstReferenceVariable?.source === 'company_traveller';
  const rawReferenceActionId =
    !isAttributeReference && referenceVariablesKeys.length > 0
      ? (referenceVariables[referenceVariablesKeys[0]] as MappableActionVariable).action
      : '';

  const referenceActionId =
    rawReferenceActionId === 'undefined' ? '' : rawReferenceActionId.replace(/_MISSING$/, '');

  const isError = isActionError(action);

  const isComplete = action.result?.status === 'success';

  return (
    <Stack space={6}>
      <HideContainer title={t('actionStatus')}>
        <ActionProgressBar
          action={action}
          isError={isError}
          errorMessage={
            isError
              ? {
                  title: actionErrorTitle[action.action],
                  description: action.result?.message,
                  date: action.result?.dateFailed,
                }
              : undefined
          }
          dateSuccess={action.result?.dateSucceeded}
        />
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <Stack space={4}>
          <LabelTextGroup label={t('description')} text={action.description} />
          <LabelTextGroup
            label={t('marker')}
            emptyText={t('noMarker')}
            text={action.values?.marker}
          />
          <Stack space={1}>
            <Text font="semibold14" text={t('text')} />
            <MappedSource
              label={firstReferenceVariable?.label ?? t('text')}
              isAttribute={isAttributeReference || isTravellerReference}
              isActionMapped={Boolean(referenceActionId)}
              isUploaded={false}
              referenceActionId={referenceActionId}
            />
          </Stack>
        </Stack>
      </HideContainer>
      {isComplete && (
        <>
          <Sidebar.Separator />
          <HideContainer title={t('anonymizedText')}>
            <Text font="regular14" text={action.savedValues.output} />
          </HideContainer>
        </>
      )}
      <Sidebar.Separator />
      <TravellerActionFooter
        action={action}
        startIcon={<IconReplace />}
        startText={t('anonymize')}
        retryText={t('retry')}
      />
    </Stack>
  );
};

type AnonymizeTextData = {
  input: EditorValue<{ variables?: FieldMappingVariables }>;
  marker: string;
  description: string;
  isAutomatic: boolean;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
};

const Form = createForm();

const FormContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const formStore = useCreateFormStore();
  const values = action.values as JourneyActionValuesTypes['anonymize_text'];
  const isFormDirty = formStore.useIsDirty();
  const isDirty = isFormDirty;
  const variableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['text', 'paragraph'],
  });
  const fieldMappingId = getFieldMappingId(action.journeyId);

  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const handleSave = async (data: AnonymizeTextData) => {
    await editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        input: data.input,
        marker: data.marker,
      },
    });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('triggerMethod')}>
          <ExecutionMethod
            assignees={action.assignees}
            isAutomatic={action.isAutomatic}
            hideLabel
            formStore={formStore}
          />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('text')}>
          <Stack space={4}>
            <Form.FieldMappingField
              id="input"
              label={t('text')}
              defaultValue={values?.input}
              singleLine
              plaintext
              variableGroups={variableGroups}
              fieldMappingId={fieldMappingId}
              validators={['required']}
            />
            <Form.TextField
              id="marker"
              defaultValue={values?.marker ?? '[REDACTED]'}
              label={t('marker')}
            />
          </Stack>
        </HideContainer>
        <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
      </Stack>
    </FormWithUnsavedChangesPrompt>
  );
};
const sidebarLocale = {
  en: {
    document: 'Document',
    dataNotFound: 'Data not found',
    description: 'Description',
    automatic: 'Automatic',
    manual: 'Manual',
    details: 'Details',
    started: 'Started',
    completed: 'Completed',
    retry: 'Re-create',
    inProgress: 'In progress',
    error: 'Error',
    notStarted: 'Not started',
    actionNotRun: 'Action has not been run',
    anonymize: 'Anonymize',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    advanced: 'Advanced',
    actionStatus: 'Action status',
    actionHasNotRun: 'Action has not been run',

    anonymizedText: 'Anonymized text',
    text: 'Input text',
    title: 'Anonymize text',
    marker: 'Redaction marker',
    noMarker: 'No marker',
  },
};
